import { useEffect, useRef } from "react";
import { customerSupportApi } from "./customerSupportApi";

/* How often the auto-sync fires when the tab is alive. */
const SYNC_INTERVAL_MS = 30 * 60 * 1000;    // 30 minutes

/* A short delay before the very first fire so we don't slam the API the
   instant the app boots — gives the login flow time to settle and lets
   the user see the UI before background work begins. */
const INITIAL_DELAY_MS = 8 * 1000;          // 8 seconds

/* Minimum gap between two consecutive fires regardless of trigger
   (interval / focus / visibility). Prevents a focus-storm from firing
   sync N times back-to-back. */
const MIN_GAP_MS = 5 * 60 * 1000;           // 5 minutes


const hasAuthToken = () => !!localStorage.getItem("authToken");

const hasCustomerSupportPermission = () => {
    try {
        const perms = JSON.parse(localStorage.getItem("userPermissions") || "[]");
        return Array.isArray(perms) && perms.includes("Customer Support");
    } catch {
        return false;
    }
};


/**
 * Global background sync for the Customer Support mirror.
 *
 * Mount this hook ONCE near the top of the React tree (Layout or App). It:
 *   • Fires `customerSupportApi.triggerSync()` ~8s after mount.
 *   • Then again every 30 minutes via `setInterval`.
 *   • Also when the tab regains focus (handy if the user leaves the tab
 *     idle for hours — they'll see fresh data immediately on return).
 *
 * Safety nets:
 *   • Skips the call if there's no `authToken` in localStorage (logged out).
 *   • Skips if the user doesn't have the "Customer Support" permission.
 *   • Refuses to overlap with an in-flight sync.
 *   • Throttles to a 5-minute floor between fires so a flurry of focus
 *     events doesn't translate into a flurry of API calls.
 *   • Swallows errors — auto-sync is best-effort background work, not
 *     something to surface as a toast.
 */
export const useCustomerSupportAutoSync = () => {
    const inFlight = useRef(false);
    const lastFiredAt = useRef(0);

    useEffect(() => {
        let cancelled = false;

        const fire = async (reason) => {
            if (cancelled) return;
            if (!hasAuthToken()) return;
            if (!hasCustomerSupportPermission()) return;
            if (inFlight.current) return;
            if (Date.now() - lastFiredAt.current < MIN_GAP_MS) return;

            inFlight.current = true;
            lastFiredAt.current = Date.now();
            try {
                await customerSupportApi.triggerSync({ refresh_registry: true });
                // Quiet on success — this is background work.
                // eslint-disable-next-line no-console
                console.debug(`[cs-sync] auto-sync ok (${reason})`);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn(`[cs-sync] auto-sync failed (${reason}):`, err?.message || err);
            } finally {
                inFlight.current = false;
            }
        };

        const initialTimer = setTimeout(() => fire("initial"), INITIAL_DELAY_MS);
        const interval = setInterval(() => fire("interval"), SYNC_INTERVAL_MS);

        const onFocus = () => fire("focus");
        const onVisibility = () => {
            if (document.visibilityState === "visible") fire("visibility");
        };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            cancelled = true;
            clearTimeout(initialTimer);
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);
};
